import React, { useEffect, useRef, useState } from 'react';

// Hooks
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import usePaymentStatusPoller from '~/hooks/usePaymentStatusPoller';

// MUI Components
import { styled } from '@mui/material/styles';
import Dialog from '@mui/material/Dialog';
import DialogContent from '@mui/material/DialogContent';
import { Box, Typography, CircularProgress, IconButton } from '@mui/material';

// Icons
import {
  AddPaymentMethod,
  CancelOrderModalCIcon,
  CloseModalIcon,
  ImportConfirmModalCIcon,
  Logout2CIcon,
} from '~/assets/images';

// Actions
import {
  CLEAR_PAYMENT_FAILED_MODAL,
  DISMISS_PAYMENT_FAILED_MODAL,
  LOGOUT,
} from '~/redux/actions/action-types';
import {
  changeDefaultPaymentMethod,
  clearPaymentMethods,
  getAllPaymentMethods,
} from '~/redux/actions/stripe-actions';
import { getUserData } from '~/redux/actions/auth-actions';
import { failure } from '~/redux/actions/snackbar-actions';

// Utils
import { MESSAGES } from '~/utils/message';
import { clearStorage } from '~/utils/local-storage';

// Components
import Stripe from '~/components//Stripe/Stripe';
import PaymentMethodBoard from '../PaymentMethodBoard';
import PaymentMethod from '../PaymentMethod';

// Styles
import './styles.scss';
import OlcButton from '~/components/General/OlcButton';

const BootstrapDialog = styled(Dialog)(({ theme }) => ({
  '& .MuiDialogContent-root': {
    padding: theme.spacing(2),
  },
  '& .MuiDialogActions-root': {
    padding: theme.spacing(1),
  },
  '& .MuiPaper-root': {
    boxShadow: 'none !important',
  },
  '& .MuiDialog-paper': {
    boxShadow: 'none !important',
  },
}));

const PaymentMethodStatusModal = ({ open, handleClose, isError = false, onRetry }) => (
  <Dialog
    aria-labelledby="transaction-error-payment-method-status"
    open={open}
    className="paymentModalWrapper transactionErrorPaymentModal"
  >
    <DialogContent className="paymentModalContent">
      <Box className="pmHeaderWallet">
        <IconButton
          aria-label="close"
          onClick={handleClose}
          className="crossIcon"
          disableRipple
          disableFocusRipple
          disableTouchRipple
          sx={{
            '&:hover': {
              backgroundColor: 'transparent',
            },
          }}
        >
          <img src={CloseModalIcon} alt="close" />
        </IconButton>
      </Box>

      <Box className="paymentHeader">
        <img src={AddPaymentMethod} alt="payment-icon" />
        <Typography gutterBottom variant="h4">
          {MESSAGES.ACCOUNT.WALLET.STRIPE.ADD_PAYMENT_METHOD}
        </Typography>
      </Box>

      <Box className="transactionErrorPaymentStatus">
        {isError ? (
          <>
            <Typography className="transactionErrorPaymentStatusText">
              {MESSAGES.ACCOUNT.WALLET.STRIPE.PAYMENT_METHOD_FORM_ERROR}
            </Typography>
            <OlcButton variant="primary" onClick={onRetry}>
              {MESSAGES.ACCOUNT.WALLET.STRIPE.TRY_AGAIN}
            </OlcButton>
          </>
        ) : (
          <CircularProgress sx={{ color: '#ed5c2f' }} />
        )}
      </Box>
    </DialogContent>
  </Dialog>
);

/**
 * TransactionErrorModal
 *
 * A BLOCKING modal that appears when a payment failure is detected.
 * - Cannot be dismissed by the user manually.
 * - Blurs the rest of the UI via CSS on the backdrop.
 * - Embeds PaymentMethodBoard so the user can fix their payment method.
 * - Dismissed only when the parent (App.jsx) clears the Redux state
 *   after a successful transaction is confirmed.
 */
const TransactionErrorModal = ({ open }) => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const [addPaymentMethodModal, setAddPaymentMethodModal] = useState(false);
  const [paymentMethodRetryKey, setPaymentMethodRetryKey] = useState(0);
  const [processingPayment, setProcessingPayment] = useState(false);

  const userId = useSelector((state) => state.userReducers?.user?.id);
  const planSuspendedAt = useSelector(
    (state) => state.userReducers?.user?.organization?.planSuspendedAt
  );
  const isPaymentFailedModalOpen = useSelector((state) => state.paymentFailureReducer?.isOpen);

  // Guards against stale set-default callbacks updating state after the user has
  // already chosen to exit the processing state (via "Try a different card") or
  // after the modal closes entirely.
  const setDefaultAbortedRef = useRef(false);

  // While the modal is open, poll POST /auth/me every 8 s. The Axios interceptor
  // detects the x-payment-failed-at header on every response and automatically
  // dispatches CLEAR_PAYMENT_FAILED_MODAL when the backend clears the lock —
  // no explicit Redux action needed here. `checking` drives the live indicator.
  // Polling is skipped when the org is permanently suspended — no invoice to recover.
  const { checking, exhausted, reset } = usePaymentStatusPoller(
    open && !planSuspendedAt,
    processingPayment
  );

  useEffect(() => {
    if (!open) {
      setDefaultAbortedRef.current = true;
      setAddPaymentMethodModal(false);
      setPaymentMethodRetryKey(0);
      setProcessingPayment(false);
      dispatch(clearPaymentMethods());
      return;
    }

    dispatch(getAllPaymentMethods());
  }, [open, dispatch]);

  useEffect(() => {
    if (!isPaymentFailedModalOpen && processingPayment) {
      setProcessingPayment(false);
    }
  }, [isPaymentFailedModalOpen, processingPayment]);

  /**
   * Called by PaymentMethod after a card is successfully attached.
   * Automatically triggers set-as-default so the server immediately retries
   * the open invoice — the user should not have to do this manually in the
   * recovery context.
   */
  const handleCardAttached = async (paymentMethodId) => {
    setDefaultAbortedRef.current = false;
    setProcessingPayment(true); // enter processing state immediately, before the request

    const response = await changeDefaultPaymentMethod(paymentMethodId);

    if (setDefaultAbortedRef.current) return;

    const isDeclined =
      response?.status === 402 && response?.headers?.['x-payment-declined'] === 'true';

    if (isDeclined) {
      setProcessingPayment(false);
      dispatch(failure(MESSAGES.ACCOUNT.WALLET.STRIPE.CARD_ALSO_DECLINED, ' '));
      setAddPaymentMethodModal(true); // reopen card form so user can try another card
      return;
    }

    if (response?.status === 200) {
      const user = response?.data?.data?.user;

      // Trust the refreshed user payload — olc-server clears planPaymentFailedAt
      // synchronously when invoices.pay succeeds, before the response is sent.
      if (!user?.organization?.planPaymentFailedAt) {
        dispatch({ type: CLEAR_PAYMENT_FAILED_MODAL });
        await dispatch(getUserData());
        setProcessingPayment(false);
      }
      return;
    }

    // Generic error (network issue, unexpected server error, etc.)
    setProcessingPayment(false);
    dispatch(
      failure(response?.data?.message || MESSAGES.ACCOUNT.WALLET.STRIPE.DEFAULT_CHANGE_ERROR, ' ')
    );
  };

  /**
   * Logout handler — mirrors exactly what the header dropdown does:
   *  1. Dispatch LOGOUT to clear Redux state (all reducers reset to initialState)
   *  2. clearStorage() wipes localStorage (tokens, etc.)
   *  3. Navigate to /login
   */
  const handleLogout = () => {
    dispatch(clearPaymentMethods());
    dispatch({ type: LOGOUT });
    clearStorage();
    navigate('/login');
  };

  return (
    <>
      <BootstrapDialog
        aria-labelledby="transaction-error-dialog"
        open={open}
        disableEscapeKeyDown
        // Prevent backdrop click from closing
        onClose={(_, reason) => {
          if (reason === 'backdropClick' || reason === 'escapeKeyDown') return;
        }}
        className={
          planSuspendedAt
            ? 'genericConfirmModal transactionErrorModal transactionErrorModalSuspended'
            : 'genericConfirmModal transactionErrorModal'
        }
        PaperProps={{
          sx: { boxShadow: 'none !important' },
        }}
      >
        <DialogContent className="modalContent transactionErrorContent">
          {planSuspendedAt ? (
            // ── Permanent suspension state ────────────────────────────────────
            // Stripe exhausted all retries; subscription was canceled.
            // User must re-subscribe — no card form or poller shown.
            <Box className="transactionErrorSuspended">
              <Box className="submitHeader">
                <img
                  src={CancelOrderModalCIcon}
                  alt={MESSAGES.ACCOUNT.WALLET.STRIPE.SUBSCRIPTION_CANCELED_ICON_ALT}
                />
              </Box>

              <Box className="modalHeader">
                <Typography gutterBottom variant="h4">
                  {MESSAGES.ACCOUNT.WALLET.STRIPE.SUBSCRIPTION_CANCELED}
                </Typography>
              </Box>

              <Box className="modalMessage">
                <Typography>
                  {MESSAGES.ACCOUNT.WALLET.STRIPE.SUBSCRIPTION_CANCELED_MESSAGE}
                </Typography>
              </Box>

              <Box className="transactionErrorSuspendedActions">
                <OlcButton
                  variant="secondary"
                  className="transactionErrorSuspendedLogoutBtn"
                  onClick={handleLogout}
                  startIcon={
                    <img
                      src={Logout2CIcon}
                      alt={MESSAGES.ACCOUNT.WALLET.STRIPE.LOGOUT_ALT}
                      style={{ width: 16, height: 16 }}
                    />
                  }
                  disableRipple
                >
                  {MESSAGES.ACCOUNT.WALLET.STRIPE.LOGOUT}
                </OlcButton>

                <OlcButton variant="primary"
                  className="transactionErrorResubscribeBtn"
                  onClick={() => navigate('/account/plan')}
                  disableRipple
                >
                  {MESSAGES.ACCOUNT.WALLET.STRIPE.RESUBSCRIBE}
                </OlcButton>
              </Box>
            </Box>
          ) : processingPayment ? (
            // ── Processing state ─────────────────────────────────────────────
            // Card was submitted successfully; backend webhook is still settling.
            // The poller will close this modal automatically once confirmed.
            <Box className="transactionErrorProcessing">
              <CircularProgress sx={{ color: '#ed5c2f' }} size={48} />
              <Typography className="transactionErrorProcessingText">
                {MESSAGES.ACCOUNT.WALLET.STRIPE.PAYMENT_PROCESSING}
              </Typography>
              <OlcButton
                variant="secondary"
                className="transactionErrorTryOtherCard"
                onClick={() => {
                  setDefaultAbortedRef.current = true;
                  setProcessingPayment(false);
                }}
                disableRipple
              >
                {MESSAGES.ACCOUNT.WALLET.STRIPE.TRY_DIFFERENT_CARD}
              </OlcButton>
            </Box>
          ) : exhausted ? (
            // ── Exhausted state ───────────────────────────────────────────────
            // Polling timed out after ~5 minutes. Stripe's own retry/confirmation
            // can take 1-3 days, so we reassure the user rather than showing an
            // error. "Close for now" dismisses the modal without clearing the
            // server-side block — the block lifts automatically when Stripe
            // confirms payment and the webhook fires.
            <Box className="transactionErrorExhausted">
              <Box className="modalHeader">
                <Typography gutterBottom variant="h4">
                  {MESSAGES.ACCOUNT.WALLET.STRIPE.STATUS_EXHAUSTED_HEADING}
                </Typography>
              </Box>

              <Box className="modalMessage">
                <Typography>{MESSAGES.ACCOUNT.WALLET.STRIPE.STATUS_EXHAUSTED_BODY}</Typography>
              </Box>

              <Box className="transactionErrorActions">
                <OlcButton variant="primary"
                  className="transactionErrorCheckNowBtn"
                  onClick={reset}
                  disableRipple
                >
                  {MESSAGES.ACCOUNT.WALLET.STRIPE.RECHECK}
                </OlcButton>
              </Box>

              <Box className="transactionErrorCloseForNow">
                <OlcButton
                  variant="secondary"
                  className="transactionErrorCloseForNowBtn"
                  onClick={() => dispatch({ type: DISMISS_PAYMENT_FAILED_MODAL })}
                  disableRipple
                >
                  {MESSAGES.ACCOUNT.WALLET.STRIPE.CLOSE_FOR_NOW}
                </OlcButton>
              </Box>
            </Box>
          ) : (
            // ── Normal locked state ───────────────────────────────────────────
            <>
              {/* Warning Icon */}
              <Box className="submitHeader">
                <img
                  src={ImportConfirmModalCIcon}
                  alt={MESSAGES.ACCOUNT.WALLET.STRIPE.TRANSACTION_FAILED_WARNING_ALT}
                />
              </Box>

              {/* Header */}
              <Box className="modalHeader">
                <Typography gutterBottom variant="h4">
                  {MESSAGES.ACCOUNT.WALLET.STRIPE.TRANSACTION_FAILED}
                </Typography>
              </Box>

              {/* Message */}
              <Box className="modalMessage">
                <Typography>{MESSAGES.ACCOUNT.WALLET.STRIPE.TRANSACTION_FAILED_MESSAGE}</Typography>
                <br />
                <Typography sx={{ mt: 1.5 }}>
                  {MESSAGES.ACCOUNT.WALLET.STRIPE.TRANSACTION_FAILED_MESSAGE_DETAIL}
                </Typography>
              </Box>

              {/* Embedded PaymentMethodBoard */}
              <Box className="transactionErrorPaymentBoard">
                <PaymentMethodBoard
                  key={userId}
                  openPaymentMethodModal={() => setAddPaymentMethodModal(true)}
                />
              </Box>

              {/* Live status indicator — pulses while polling */}
              <Box className={`transactionErrorStatus ${checking ? 'isChecking' : 'isMonitoring'}`}>
                <span className="transactionErrorStatusDot" />
                <Typography className="transactionErrorStatusText">
                  {checking
                    ? MESSAGES.ACCOUNT.WALLET.STRIPE.STATUS_CHECKING
                    : MESSAGES.ACCOUNT.WALLET.STRIPE.STATUS_MONITORING}
                </Typography>
              </Box>

              {/* Logout button */}
              <Box className="transactionErrorLogout">
                <OlcButton
                  variant="secondary"
                  className="transactionErrorLogoutBtn"
                  onClick={handleLogout}
                  startIcon={
                    <img
                      src={Logout2CIcon}
                      alt={MESSAGES.ACCOUNT.WALLET.STRIPE.LOGOUT_ALT}
                      style={{ width: 16, height: 16 }}
                    />
                  }
                  disableRipple
                >
                  {MESSAGES.ACCOUNT.WALLET.STRIPE.LOGOUT}
                </OlcButton>
              </Box>
            </>
          )}
        </DialogContent>
      </BootstrapDialog>

      {addPaymentMethodModal && (
        <Stripe
          key={paymentMethodRetryKey}
          errorFallback={
            <PaymentMethodStatusModal
              open={addPaymentMethodModal}
              isError
              handleClose={() => {
                setAddPaymentMethodModal(false);
              }}
              onRetry={() => {
                setPaymentMethodRetryKey((prev) => prev + 1);
              }}
            />
          }
        >
          <PaymentMethod
            open={addPaymentMethodModal}
            className="transactionErrorPaymentModal"
            handleOpen={() => setAddPaymentMethodModal(true)}
            handleClose={() => setAddPaymentMethodModal(false)}
            onSuccessMethod={handleCardAttached}
            onPaymentProcessing={() => {
              setAddPaymentMethodModal(false);
              setProcessingPayment(true);
            }}
          />
        </Stripe>
      )}
    </>
  );
};

export default TransactionErrorModal;
